import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, InputAdornment, TextField, Box } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useSelector } from 'react-redux';
import { useSearchInsightPolicyTransactionsInfiniteQuery } from '@/features/policies/policiesApi';
import { FILTER_TYPES } from '@/features/policies/data';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { formatDateDayToYear } from '@/utils/constants';
import { safelyParseDate } from '@/utils/date';

export const PolicyTransactionSelect = ({
	policyId,
	policyTransaction,
	setPolicyTrans,
	disabled,
}) => {
	const [open, setOpen] = useState(false);
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isLoading,
		isFetching,
		isFetchingNextPage,
		isUninitialized,
	} = useSearchInsightPolicyTransactionsInfiniteQuery(
		{
			...(!userDetails?.canViewGroup && { authRepId: userDetails?.authorisedRepId }),
			policyId,
			filterBy: FILTER_TYPES.ShowAll,
		},
		{ skip: !policyId || !userDetails },
	);

	useEffect(() => {
		if (
			!policyId ||
			isUninitialized ||
			!hasNextPage ||
			isFetching ||
			!data?.pages?.length
		) {
			return;
		}

		fetchNextPage();
	}, [policyId, isUninitialized, data?.pages?.length, hasNextPage, isFetching, fetchNextPage]);

	const policyOptions = useMemo(() => {
		const rows = data?.pages.flatMap((page) => page.Results ?? []) ?? [];

		return [...rows].sort((a, b) => {
			const dateA =
				safelyParseDate(a.PolicyEffectiveDate ?? a.EffectiveDate)?.getTime?.() ?? 0;
			const dateB =
				safelyParseDate(b.PolicyEffectiveDate ?? b.EffectiveDate)?.getTime?.() ?? 0;

			if (dateA !== dateB) {
				return dateB - dateA;
			}

			return (b.InvoiceNumber ?? 0) - (a.InvoiceNumber ?? 0);
		});
	}, [data]);

	const isLoadingOptions = isLoading || isFetchingNextPage || (isFetching && hasNextPage);

	const formatTransactionDate = (dateValue) => {
		const date = safelyParseDate(dateValue);
		return date ? formatDateDayToYear(date) : '';
	};

	const getFormattedString = (policyTrans) => {
		// console.log('policy trans data: ', policyTrans);

		if (Object.keys(policyTrans ?? {}).length > 0) {
			// Search Insight transactions use Policy* date fields; retrieve/APIs may use unprefixed names
			return `INV ${policyTrans?.InvoiceNumber ?? ''} - ${formatTransactionDate(policyTrans?.PolicyFromDate ?? policyTrans?.FromDate)} to ${formatTransactionDate(
				policyTrans?.PolicyToDate ?? policyTrans?.ToDate,
			)} (Effective ${formatTransactionDate(policyTrans?.PolicyEffectiveDate ?? policyTrans?.EffectiveDate)}), ${
				policyTrans?.TransactionTypeName ?? policyTrans?.TransactionType
			}, ${policyTrans?.PolicyNumber} - ${policyTrans?.InsurerName}`;
		}
		return '';
	};

	return (
		<Autocomplete
			id='policy-select'
			loading={isLoadingOptions}
			loadingText='Loading policy transactions...'
			options={policyOptions}
			getOptionLabel={(policyTrans) => getFormattedString(policyTrans)}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			renderOption={(props, policyTrans) => (
				<Box component='li' {...props}>
					{getFormattedString(policyTrans)}
				</Box>
			)}
			value={Object.keys(policyTransaction ?? {}).length > 0 ? policyTransaction : null}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder='Search for policy'
					// sx={{ opacity: !policyTransData ? '0.5' : '1' }}
					InputProps={{
						...params.InputProps,
						...(isLoadingOptions && {
							startAdornment: (
								<InputAdornment position='start'>
									<CircularProgress color='inherit' size={20} />
								</InputAdornment>
							),
						}),
					}}
				/>
			)}
			isOptionEqualToValue={(option, value) => option.Id === value.Id}
			onChange={(_, value) => {
				setPolicyTrans(value);
			}}
			disabled={!policyId || disabled}
		/>
	);
};
